import type { FiatCurrency } from '@0xtorch/core'
import type { ActionFilter, HistoryPageSort } from '@pkg/basic'
import { asc, desc, inArray, sql } from 'drizzle-orm'
import { parseToActionWithTransactionList } from '../parsers/action'
import { parseToActionSource } from '../parsers/actionSource'
import { actionSourceTable, actionTable } from '../schema'
import { andSqlConditions } from '../sqlConditions'
import type { ActionGroup, DatabaseWithTransaction } from '../types'
import {
  createDecimalSortKey,
  createWhereConditionsByActionFilterList,
} from '../utils'

const zeroDecimalSortKey = createDecimalSortKey({ value: '0' })

type SelectActionGroupListByFilterListParameters = {
  readonly database: DatabaseWithTransaction
  readonly filterList: ActionFilter[]
  readonly offset: number
  readonly limit: number
  readonly sort?: HistoryPageSort
  readonly fiatCurrencyList: readonly FiatCurrency[]
}

export const selectActionGroupListByFilterList = async ({
  database: { database },
  filterList,
  offset,
  limit,
  sort,
  fiatCurrencyList,
}: SelectActionGroupListByFilterListParameters) => {
  const whereConditions = createWhereConditionsByActionFilterList(filterList)
  const where = andSqlConditions({ conditions: whereConditions })

  const maxTimestampSql = sql<number>`max(${actionTable.timestamp})`
  const totalPlSumSql = sql<number>`sum(cast(coalesce(${actionTable.totalPl}, '0') as real))`

  // サブクエリで sourceId のリストをページング付きで取得
  const sourceIdRows =
    sort?.type === 'pl'
      ? await database
          .select({
            source: actionTable.sourceId,
            timestamp: maxTimestampSql,
            totalPl: totalPlSumSql,
          })
          .from(actionTable)
          .where(where)
          .groupBy(actionTable.sourceId)
          .orderBy(
            sort.order === 'desc' ? desc(totalPlSumSql) : asc(totalPlSumSql),
            desc(maxTimestampSql),
          )
          .limit(limit)
          .offset(offset)
      : await database
          .select({
            source: actionTable.sourceId,
            timestamp: maxTimestampSql,
          })
          .from(actionTable)
          .where(where)
          .groupBy(actionTable.sourceId)
          .orderBy(
            sort === undefined || sort.order === 'desc'
              ? desc(maxTimestampSql)
              : asc(maxTimestampSql),
          )
          .limit(limit)
          .offset(offset)

  const targetSourceList = sourceIdRows.map((row) => row.source)
  if (targetSourceList.length === 0) {
    return []
  }

  const result = await database.query.actionSourceTable.findMany({
    where: inArray(actionSourceTable.id, targetSourceList),
    with: {
      actionTable: {
        with: {
          actionTransferTable: {
            with: {
              cryptoCurrencyTable: true,
              nftTable: true,
            },
          },
          nftTable: true,
          transactionList: {
            with: {
              transferList: {
                with: {
                  cryptoCurrency: true,
                  nft: true,
                },
              },
              nft: true,
            },
          },
        },
      },
    },
  })

  // parse to action list
  const actionList = result.flatMap((actionSource) => {
    const source = parseToActionSource(actionSource)
    return actionSource.actionTable.map((data) =>
      parseToActionWithTransactionList(data, fiatCurrencyList, source),
    )
  })

  // grouping actions by action source
  const mut_actionGroupList: ActionGroup[] = []
  for (const action of actionList) {
    const sourceId = action.source.id
    const actionGroupIndex = mut_actionGroupList.findIndex(
      (actionGroup) => actionGroup.id === sourceId,
    )
    if (actionGroupIndex === -1) {
      mut_actionGroupList.push({
        id: sourceId,
        timestamp: action.timestamp,
        source: action.source,
        app: action.app,
        actionList: [action],
      })
    } else {
      const actionGroup = mut_actionGroupList[actionGroupIndex]
      mut_actionGroupList[actionGroupIndex] = {
        ...actionGroup,
        app: actionGroup.app ?? action.app,
        actionList: [...actionGroup.actionList, action].toSorted(
          (a, b) => a.order - b.order,
        ),
      }
    }
  }

  const sourceOrder = new Map(
    sourceIdRows.map((row, index) => [row.source, index]),
  )

  return mut_actionGroupList.sort((left, right) => {
    const leftIndex = sourceOrder.get(left.id)
    const rightIndex = sourceOrder.get(right.id)

    if (leftIndex === undefined && rightIndex === undefined) {
      return 0
    }
    if (leftIndex === undefined) {
      return 1
    }
    if (rightIndex === undefined) {
      return -1
    }

    return leftIndex - rightIndex
  })
}
